import React from 'react';
import {
  LayoutDashboard,
  Database,
  Lock,
  Table,
  GitFork,
  LineChart,
  Radio,
  ShieldCheck,
  Wrench,
  ChevronLeft,
  ChevronRight,
  type LucideIcon,
  Layers,
  TableOfContents,
  HardDrive,
  Cpu,
  Activity,
  Users,
  UserCheck,
  Terminal,
} from 'lucide-react';

export interface SidebarItem {
  id: string;
  label: string;
  icon: LucideIcon;
  subItems?: any[];
}

export interface SidebarGroup {
  id: string;
  label: string;
  icon: LucideIcon;
  subItems?: any[];
}

interface DBSidebarProps {
  activeSidebar: string;
  onSelectSidebar: (id: string) => void;
  isOpen?: boolean;
  onToggleOpen?: () => void;
  dbType?: string;
}

export const sidebarItems: SidebarItem[] = [
  { id: 'topology', label: 'Topology', icon: GitFork },
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'metrics', label: 'Server Performance', icon: LineChart },
  { id: 'databases', label: 'Database Performance', icon: Database },
  { id: 'tempdb', label: 'Temporary DB', icon: Layers },
  { id: 'memory', label: 'Memory', icon: Cpu },
  { id: 'schedulers', label: 'Schedulers', icon: Activity },
  { id: 'file_io', label: 'File IO', icon: HardDrive },
  { id: 'indexes', label: 'Indexes', icon: TableOfContents },
  { id: 'storage', label: 'Storage & Maintenance', icon: HardDrive },
  { id: 'esindices', label: 'Indices', icon: Layers },
  { id: 'blocking', label: 'Queries', icon: Lock },
  { id: 'users', label: 'Users & Logins', icon: UserCheck },
  { id: 'user_queries', label: 'User Query Statistics', icon: Users },
  { id: 'schema', label: 'Schema', icon: Table },
  { id: 'connections', label: 'Active Connections', icon: Radio },
  { id: 'logs', label: 'Log Monitor', icon: Terminal },
  { id: 'health', label: 'Agent Health', icon: ShieldCheck },
  { id: 'configuration', label: 'Configuration', icon: Wrench },
];

export const sidebarGroups: SidebarGroup[] = sidebarItems;

export const DBSidebar: React.FC<DBSidebarProps> = ({
  activeSidebar,
  onSelectSidebar,
  isOpen = true,
  onToggleOpen,
  dbType,
}) => {
  const [hoveredTab, setHoveredTab] = React.useState<{ label: string; top: number } | null>(null);

  const dbLower = (dbType || '').toLowerCase();
  const isES = ['opensearch', 'elasticsearch'].includes(dbLower);
  const isMongoDB = ['mongodb', 'mongo'].includes(dbLower);
  const isMSSQL = ['mssql', 'sqlserver', 'sql server', 'sql_server'].includes(dbLower) || dbLower.includes('mssql');
  const isMySQL = ['mysql', 'mariadb'].includes(dbLower) || dbLower.includes('mysql');

  const visibleItems = sidebarItems
    .filter((item) => {
      if (item.id === 'tempdb' || item.id === 'memory') {
        return isMSSQL;
      }
      if (item.id === 'schedulers') {
        return isMSSQL || isMySQL;
      }
      if (item.id === 'esindices') {
        return isES;
      }
      if (isES && ['blocking', 'databases', 'indexes', 'storage',].includes(item.id)) {
        return false;
      }
      if (isMongoDB && ['esindices', 'storage',].includes(item.id)) {
        return false;
      }
      return true;
    })
    .map((item) => {
      if (item.id === 'databases' && isMongoDB) {
        return { ...item, label: 'Collections' };
      }
      return item;
    });

  return (
    <>
      <aside className="w-full space-y-1 select-none flex flex-col h-full">
        {/* Sidebar Header with Collapse / Expand Toggle */}
        <div
          className={`flex items-center pb-2 mb-1 border-b border-gray-800/80 ${isOpen ? 'justify-between px-1' : 'justify-center'
            }`}
        >
          {isOpen && (
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
              Navigation
            </span>
          )}
          {onToggleOpen && (
            <button
              onClick={onToggleOpen}
              className="p-1 rounded-lg bg-gray-800/70 hover:bg-gray-700/80 text-gray-300 hover:text-white border border-gray-700/60 transition-all shadow-sm"
              title={isOpen ? 'Collapse Sidebar' : 'Expand Sidebar'}
            >
              {isOpen ? <ChevronLeft className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>

        {/* Direct Flat List of All Items */}
        <div className="space-y-0.5 flex-1">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSidebar === item.id;

            return (
              <button
                key={item.id}
                onMouseEnter={(e) =>
                  !isOpen &&
                  setHoveredTab({ label: item.label, top: e.currentTarget.getBoundingClientRect().top })
                }
                onMouseLeave={() => setHoveredTab(null)}
                onClick={() => {
                  onSelectSidebar(item.id);
                  setHoveredTab(null);
                }}
                title={!isOpen ? item.label : undefined}
                className={`w-full flex items-center gap-2 rounded-lg text-xs transition-colors duration-150 border ${isOpen ? 'px-2.5 py-1.5 justify-start' : 'px-2 py-1.5 justify-center'
                  } ${isActive
                    ? 'bg-orange-600 border-orange-500/40 text-white font-bold shadow-md'
                    : 'border-transparent hover:bg-gray-800/50 text-gray-200 hover:text-white font-medium'
                  }`}
              >
                <Icon className={`w-3.5 h-3.5 shrink-0 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                {isOpen && <span className="truncate">{item.label}</span>}
              </button>
            );
          })}
        </div>
      </aside>

      {/* Floating Tooltip when Sidebar is Collapsed */}
      {!isOpen && hoveredTab && (
        <div
          className="fixed z-[9999] px-2.5 py-1.5 bg-orange-600 text-white text-[11px] font-bold rounded-lg shadow-2xl whitespace-nowrap pointer-events-none transition-opacity duration-200 border border-orange-500/50"
          style={{ left: '4.25rem', top: hoveredTab.top + 2 }}
        >
          {hoveredTab.label}
          <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-orange-600 rotate-45 rounded-sm"></div>
        </div>
      )}
    </>
  );
};

export default DBSidebar;
